import React, { useState, useEffect } from 'react';
import { Mail, Copy, Check, Trash2, AlertCircle } from 'lucide-react';
import {
  listEmailAddresses,
  deleteEmailAddress,
} from '../../services/emailAddressService';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

/**
 * Component to display and manage inbound email addresses
 *
 * @param {Object} props
 * @param {string} [props.executiveId] - Filter by executive ID
 * @param {string} [props.tripId] - Filter by trip ID
 * @param {Function} [props.onEmailCreated] - Callback to refresh list
 */
const EmailAddressList = ({ executiveId, tripId, onEmailCreated }) => {
  const [emailAddresses, setEmailAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);

  const loadEmailAddresses = async () => {
    setLoading(true);
    setError('');

    try {
      const filters = {};
      if (executiveId) filters.executive_id = executiveId;
      if (tripId) filters.trip_id = tripId;

      const result = await listEmailAddresses(filters);

      if (result.success) {
        setEmailAddresses(result.inbound_email_addresses || []);
      }
    } catch (err) {
      setError('Failed to load email addresses');
      console.error('Error loading email addresses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmailAddresses();
  }, [executiveId, tripId]);

  // Reload when onEmailCreated is called
  useEffect(() => {
    if (onEmailCreated) {
      loadEmailAddresses();
    }
  }, [onEmailCreated]);

  const handleCopy = async (emailAddress, id) => {
    try {
      await navigator.clipboard.writeText(emailAddress);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const confirmDelete = (email) => {
    setEmailToDelete(email);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!emailToDelete) return;

    try {
      await deleteEmailAddress(emailToDelete.id);
      setEmailAddresses((prev) => prev.filter((e) => e.id !== emailToDelete.id));
      setDeleteDialogOpen(false);
      setEmailToDelete(null);
    } catch (err) {
      console.error('Error deleting email address:', err);
      setError('Failed to delete email address');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 p-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (emailAddresses.length === 0) {
    return (
      <div className="rounded-lg border-2 border-dashed border-slate-200 p-8 text-center">
        <Mail className="h-12 w-12 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-600">No email addresses created yet</p>
        <p className="text-xs text-slate-500 mt-1">
          Create an email address to start receiving confirmations
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {emailAddresses.map((email) => (
          <div
            key={email.id}
            className="rounded-lg border border-slate-200 bg-white p-4 hover:border-violet-200 transition-colors"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4 text-violet-600 flex-shrink-0" />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    {email.email_type === 'executive' ? 'Executive Email' : 'Trip Email'}
                  </span>
                  {!email.is_active && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                      Inactive
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <code className="text-sm font-mono text-slate-900 break-all">
                    {email.email_address}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 flex-shrink-0"
                    onClick={() => handleCopy(email.email_address, email.id)}
                  >
                    {copiedId === email.id ? (
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600">
                  {email.email_type === 'executive' && email.executive_name && (
                    <div>
                      <span className="font-semibold">Executive:</span> {email.executive_name}
                    </div>
                  )}
                  {email.email_type === 'trip' && email.trip_title && (
                    <div>
                      <span className="font-semibold">Trip:</span> {email.trip_title}
                    </div>
                  )}
                  <div>
                    <span className="font-semibold">Emails received:</span> {email.total_emails_received || 0}
                  </div>
                  <div>
                    <span className="font-semibold">Last received:</span>{' '}
                    {formatDate(email.last_email_received_at)}
                  </div>
                  <div>
                    <span className="font-semibold">Created:</span> {formatDate(email.created_at)}
                  </div>
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => confirmDelete(email)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Email Address?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the email address{' '}
              <strong className="text-slate-900">{emailToDelete?.email_address}</strong>. Emails
              sent to this address will no longer be processed.
              <br />
              <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EmailAddressList;
